// Import GSAP core and the ScrollTrigger plugin
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SplitType from "split-type";

// Register the ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

// Check if the user prefers reduced motion
const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Grouped animation targets
// Tied to ACF Field Group: Clone: Animation Settings
const animateFadeIn = document.querySelectorAll('[data-animate="fadeIn"]');
const animateFadeInUp = document.querySelectorAll('[data-animate="fadeInUp"]');
const animateScaleIn = document.querySelectorAll('[data-animate="scaleIn"]');
const animateSlideInLeft = document.querySelectorAll('[data-animate="slideInLeft"]');
const animateSlideInRight = document.querySelectorAll('[data-animate="slideInRight"]');
const animateScrubFadeInRight = document.querySelectorAll('[data-animate="scrubFadeInRight"]');
const animateScrubFadeInLeft = document.querySelectorAll('[data-animate="scrubFadeInLeft"]');
const animateScrubFadeInTop = document.querySelectorAll('[data-animate="scrubFadeInTop"]');
// const animateScrubFadeInBottom = document.querySelectorAll('[data-animate="scrubFadeInBottom"]');
const animateFooter = document.querySelectorAll('[data-animate="footer"]');

// Helper function to apply animations with consistent triggers
function createAnimation(targets, animationProps, triggerProps) {
    if (prefersReducedMotion) {
        // For users who prefer reduced motion, immediately set final styles (disable animations)
        targets.forEach((block) => {
            gsap.set(block, { clearProps: "all" }); // Remove GSAP animations, set final state
        });
    } else {
        // Apply normal animations if reduced motion is not preferred
        targets.forEach((block) => {
            gsap.from(block, {
                ...animationProps,
                scrollTrigger: {
                    trigger: block,
                    ...triggerProps,
                },
            });
        });
    }
}

// This helper function assists GSAP in calculating the position of images with lazy loading, ensuring the correct trigger positioning for animations.
function handleLazyLoad(config={}) {
    let lazyImages = gsap.utils.toArray("img[loading='lazy']"),
        timeout = gsap.delayedCall(config.timeout || 1, ScrollTrigger.refresh).pause(),
        lazyMode = config.lazy !== false,
        imgLoaded = lazyImages.length,
        onImgLoad = () => lazyMode ? timeout.restart(true) : --imgLoaded || ScrollTrigger.refresh();
    lazyImages.forEach((img, i) => {
        lazyMode || (img.loading = "eager");
        img.naturalWidth ? onImgLoad() : img.addEventListener("load", onImgLoad);
    });
}

// Setup for all animations
function setupAnimations() {
    // Kill all previous ScrollTriggers before re-initializing
    ScrollTrigger.getAll().forEach((trigger) => trigger.kill());

    // Fade-in animation
    createAnimation(animateFadeIn, {
        opacity: 0,
        duration: 1,
        ease: 'cubic-bezier(.19, 1, .22, 1)',
    }, {
        start: 'top 80%',
        toggleActions: 'play none none reverse',
    });

    // Fade-in-up animation
    createAnimation(animateFadeInUp, {
        y: 100,
        opacity: 0,
        duration: 1,
        ease: 'cubic-bezier(.19, 1, .22, 1)',
    }, {
        start: 'top 80%',
        toggleActions: 'play none none reverse',
    });

    // Scale-in animation
    createAnimation(animateScaleIn, {
        y: 100,
        opacity: 0,
        scale: 1.15,
        duration: 0.5,
        ease: 'cubic-bezier(.19, 1, .22, 1)',
    }, {
        start: 'top 80%',
        toggleActions: 'play none none reverse',
    });

    // Slide in from left animation
    createAnimation(animateSlideInLeft, {
        x: -100,
        opacity: 0,
        duration: 1,
        ease: 'cubic-bezier(.19, 1, .22, 1)',
    }, {
        start: 'top 80%',
        toggleActions: 'play none none reverse',
    });

    // Slide in from right animation
    createAnimation(animateSlideInRight, {
        x: 100,
        opacity: 0,
        duration: 1,
        ease: 'cubic-bezier(.19, 1, .22, 1)',
    }, {
        start: 'top 80%',
        toggleActions: 'play none none reverse',
    });

    // Scrub from right
    createAnimation(animateScrubFadeInRight, {
        x: -200,
        opacity: 0,
        duration: 1.5,
        ease: 'power2.out',
    }, {
        start: 'top 90%',
        end: 'bottom top',
        scrub: 1,
    });

    // Scrub from left
    createAnimation(animateScrubFadeInLeft, {
        x: 200,
        opacity: 0,
        duration: 1.5,
        ease: 'power2.out',
    }, {
        start: 'top 90%',
        end: 'bottom top',
        scrub: 1,
    });

    // Scrub from top
    createAnimation(animateScrubFadeInTop, {
        y: 200,
        opacity: 0,
        duration: 1.5,
        ease: 'power2.out',
    }, {
        start: 'top 90%',
        end: 'bottom top',
        scrub: 1,
    });

    // Scrub from bottom
    // createAnimation(animateScrubFadeInBottom, {
    //     y: -200,
    //     opacity: 0,
    //     duration: 1.5,
    //     ease: 'power2.out',
    // }, {
    //     start: 'top 90%',
    //     end: 'bottom top',
    //     scrub: 1,
    // });

    // Fade-in footer logo
    createAnimation(animateFooter, {
        opacity: 0,
        duration: 1,
        ease: 'cubic-bezier(.19, 1, .22, 1)',
    }, {
        start: 'top 90%',
        toggleActions: 'play none none reverse',
    });

    // setupSlowDragAnimation();
    setupSlowDragSplitContentAnimation();
    animateTextLines();
    animateEachChildNode();

    // Handle lazy-loaded images
    handleLazyLoad({ lazy: false, timeout: 1 });
}

// Setup for slow drag animation
// function setupSlowDragAnimation() {
//     const animateSlowDrag = document.querySelectorAll('[data-animate="slowDrag"]');

//     if (animateSlowDrag.length === 0) return; // Ensure elements exist

//     if (window.innerWidth >= 992) {
//         animateSlowDrag.forEach((block) => {
//             const blockBody = block.querySelector('.block-body');
//             const blockHeight = block.offsetHeight;
//             const translateY = Math.min(window.innerHeight * 0.4, blockHeight);

//             if (blockBody) {
//                 gsap.to(blockBody, {
//                     y: translateY,
//                     ease: 'none',
//                     scrollTrigger: {
//                         trigger: blockBody,
//                         start: 'top 50%',
//                         end: 'bottom top',
//                         scrub: true,
//                     },
//                 });
//             }
//         });
//     }
// }


// Setup for slow drag animation
function setupSlowDragSplitContentAnimation() {
    const animateSlowDrag = document.querySelectorAll('[data-animate="slowDragBlock"]');

    if (animateSlowDrag.length === 0) return; // Ensure elements exist

    if (window.innerWidth >= 992) {
        animateSlowDrag.forEach((block) => {
            const blockBody = block.querySelector('.block-body');
            const blockHeight = block.offsetHeight;
            const translateY = Math.min(window.innerHeight * 0.4);

            if (blockBody) {
                gsap.to(blockBody, {
                    y: translateY,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: blockBody,
                        start: 'top 100%',
                        end: 'bottom top',
                        scrub: true,
                        markers: true,
                    },
                });
            }
        });
    }
}

// Function to animate paragraph, blockquotes, and citations
function animateTextLines() {
    const containers = document.querySelectorAll('.animate-text');

    if (containers.length === 0) {
        return;
    }

    containers.forEach((container) => {
        const headings = container.querySelectorAll('h1, h2, h3, h4, h5, h6');
        const blockquote = container.querySelector('blockquote p');
        const paragraphs = container.querySelectorAll('p');
        const cite = container.querySelector('footer');

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: container,
                start: 'top 80%',
                end: 'bottom 100%',
                toggleActions: 'play none none reverse',
            }
        });

        if (headings && headings.length > 0) {
            headings.forEach((heading) => {
                const splitText = new SplitType(heading, { types: 'lines' });
                const lines = splitText.lines;

                if (lines && lines.length > 0) {
                    tl.fromTo(
                        lines,
                        { y: 50, opacity: 0 },
                        { y: 0, opacity: 1, stagger: 0.2, duration: 1, ease: 'power4.out' }
                    );
                }
            });
        }

        if (blockquote) {
            const blockText = new SplitType(blockquote, { types: 'lines' });
            const lines = blockText.lines;

            if (lines && lines.length > 0) {
                tl.fromTo(
                    lines,
                    { y: 50, opacity: 0 },
                    { y: 0, opacity: 1, stagger: 0.3, duration: 1, ease: 'power4.out' }
                );
            }
        }

        if (paragraphs && paragraphs.length > 0) {
            paragraphs.forEach((paragraph) => {
                if (paragraph !== blockquote) {
                    const splitText = new SplitType(paragraph, { types: 'lines' });
                    const lines = splitText.lines;

                    if (lines && lines.length > 0) {
                        tl.fromTo(
                            lines,
                            { y: 50, opacity: 0 },
                            { y: 0, opacity: 1, stagger: 0.2, duration: 1, ease: 'power4.out' },
                            '-=0.8'
                        );
                    }
                }
            });
        }

        if (cite) {
            tl.fromTo(
                cite,
                { y: 50, opacity: 0 },
                { y: 0, opacity: 1, duration: 0.8, ease: 'power4.out' },
                '-=0.8'
            );
        }
    });
}

// Function to animate heading, paragraph, and button without line-by-line animation
function animateEachChildNode() {
    const containers = document.querySelectorAll('.animate-fade-in');

    if (containers.length === 0) {
        return;
    }

    containers.forEach((container) => {
        const heading = container.querySelectorAll('h1, h2, h3, h4, h5, h6');
        const paragraph = container.querySelectorAll('p');
        const button = container.querySelectorAll('.btn');

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: container,
                start: 'top 80%',
                end: 'bottom 100%',
                toggleActions: 'play none none reverse',
            }
        });

        // Animate headings if found
        if (heading.length > 0) {
            tl.fromTo(
                heading,
                { opacity: 0, y: 50 },
                { opacity: 1, y: 0, duration: 1, ease: 'power4.out' }
            );
        }

        // Animate paragraphs if found
        if (paragraph.length > 0) {
            tl.fromTo(
                paragraph,
                { opacity: 0, y: 50 },
                { opacity: 1, y: 0, duration: 1, ease: 'power4.out' },
                '-=0.8'
            );
        }

        // Animate buttons if found
        if (button.length > 0) {
            tl.fromTo(
                button,
                { opacity: 0, y: 50 },
                { opacity: 1, y: 0, duration: 1, ease: 'power4.out' },
                '-=0.8'
            );
        }
    });
}

// Initialize animations and handle resize events
setupAnimations();

window.addEventListener('resize', () => {
    // Recalculate ScrollTrigger positions on resize
    ScrollTrigger.refresh();
});
